/**
 * MER-708: the Executive board card drill's pure logic.
 *
 * public.board_drill (migration 0071) holds, for one board line and month,
 * OUR rows by customer, plus a LINE TOTAL row carrying the status, the reason
 * and our total. The pipeline (audit/board_drill.py) computes every value.
 * This module only reads them back: it groups, formats and words the panel.
 * It never derives a number of ours and never turns a missing value into a
 * zero.
 *
 * THE ONE PIECE OF ARITHMETIC HERE is the difference between our total and
 * the card. Both numbers are already published, on screen, in the same unit:
 * the card is the client's own workbook cell (public.exec_kpi, which the page
 * already holds) and our total is a stored column. Subtracting two displayed
 * numbers is not a second computation of a metric, and it is the only way the
 * reader can see whether the two sides agree. The client figure is never
 * stored in board_drill, so it stays published in exactly one place.
 *
 * What this module refuses to do:
 *   * It never shows a per-customer share of the CLIENT's number. The client
 *     line has no customer grain; splitting it would be inventing detail.
 *   * It never renders a percentage difference against a client value of
 *     zero, and never against a card with no number at all.
 *   * It never presents the drill as a breakdown of the card. The wording is
 *     "our rows for the same scope", because that is what it is.
 *
 * Plain .mjs so node --test drives it with synthetic rows (the execKpi.mjs and
 * boardVariance.mjs precedent); boardDrill.d.mts types it for the page.
 */

import { fmtValue, monthLabel } from "./execKpi.mjs";

export const BOARD_DRILL_TABLE = "board_drill";

/**
 * The columns the page reads. Every name is a column of public.board_drill in
 * 0071 (pinned by scripts/board-drill.test.mjs).
 */
export const BOARD_DRILL_COLUMNS = [
  "sheet",
  "client_label",
  "period",
  "customer_id",
  "customer_name",
  "is_line_total",
  "is_remainder",
  "rank",
  "value",
  "unit",
  "division",
  "equipment_type",
  "is_rollup",
  "status",
  "drill_basis",
  "reason",
  "our_total",
  "our_customers",
  "remainder_customers",
  "scope_note",
].join(",");

/** One sheet and month of drill rows fits in a single page many times over. */
export const DRILL_PAGE_SIZE = 1000;
export const DRILL_MAX_PAGES = 8;

export const STATUS_OK = "ok";
export const STATUS_NO_ROWS = "no_rows";
export const STATUS_NOT_DERIVABLE = "not_derivable";

export const BASIS_EARNED_REVENUE = "invoice_earned_revenue";
export const BASIS_WORK_UNITS = "work_orders_completed";

/** What each basis counts, in the reader's words. */
export const BASIS_LABELS = Object.freeze({
  [BASIS_EARNED_REVENUE]: "Meridian earned revenue, by customer",
  [BASIS_WORK_UNITS]: "Meridian completed work orders, by customer",
});

export const BASIS_NOTES = Object.freeze({
  [BASIS_EARNED_REVENUE]:
    "Invoice lines placed in the month the service was performed, not the month they were billed.",
  [BASIS_WORK_UNITS]:
    "Completed work orders in this line of business's own unit: a lift for front load, a haul for roll off, a stop for residential. Never added across services.",
});

export const PANEL_TITLE = "Customer detail behind this card";
export const NO_DETAIL_TITLE = "No customer detail for this line";
export const NOT_LOADED_TITLE = "Customer detail is not available yet";
export const NOT_LOADED_CAPTION =
  "The customer detail table is not in this database yet. Once it is, the next refresh builds it.";
export const READ_FAILED_TITLE = "Customer detail could not be read";
export const READ_FAILED_CAPTION =
  "The read failed. The details are in the browser console; nothing on this panel was guessed.";

/**
 * The standing caveat under every drill table. It is not a breakdown, and
 * saying so is the whole honesty of this panel.
 */
export const DRILL_CAVEAT =
  "This is not a breakdown of the card. The card is the client's own workbook figure, which carries no customer detail. The table is Meridian's own rows for the same division, line of business and month, so the two totals can be compared.";

export const TOTAL_ROW_LABEL = "Meridian total";
export const CARD_ROW_LABEL = "Workbook card";
export const DIFFERENCE_ROW_LABEL = "Difference";

/* ------------------------------------------------------------------ */
/* Small coercions                                                     */
/* ------------------------------------------------------------------ */

function toNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function text(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

function isMonthKey(v) {
  return typeof v === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
}

/* ------------------------------------------------------------------ */
/* Indexing a sheet and month of rows                                  */
/* ------------------------------------------------------------------ */

/**
 * Group a sheet-and-month slice of board_drill by client label.
 *
 * @param {object[]} rows
 * @returns {Map<string, {label: string, total: object|null, customers: object[]}>}
 *   keyed by client_label. `total` is the line-total row, null when the slice
 *   carries none (which the page reads as "no answer for this line", never as
 *   "derivable with no customers").
 */
export function indexDrill(rows) {
  const out = new Map();
  for (const row of rows ?? []) {
    const label = text(row?.client_label);
    if (label === null || !isMonthKey(row.period)) continue;
    let entry = out.get(label);
    if (!entry) {
      entry = { label, total: null, customers: [] };
      out.set(label, entry);
    }
    if (row.is_line_total === true) entry.total = row;
    else entry.customers.push(row);
  }
  for (const entry of out.values()) {
    entry.customers.sort((a, b) => {
      // The remainder always sits last, whatever its rank.
      if ((a.is_remainder === true) !== (b.is_remainder === true)) {
        return a.is_remainder === true ? 1 : -1;
      }
      const ra = toNumber(a.rank);
      const rb = toNumber(b.rank);
      if (ra === null || rb === null) return 0;
      return ra - rb;
    });
  }
  return out;
}

/**
 * Can this card be clicked? True only when the slice carries a line total
 * that says the line is derivable. A line with no row at all is NOT clickable:
 * a drill that cannot be honoured must never be advertised (the MER-571 rule
 * the Overview drill already follows).
 */
export function isDrillable(entry) {
  const status = entry?.total?.status;
  return status === STATUS_OK || status === STATUS_NO_ROWS;
}

/** The short chip a non-derivable card carries, or null when it drills. */
export function cardNote(entry) {
  if (entry?.total == null) return null;
  if (isDrillable(entry)) return null;
  return "No customer detail";
}

/* ------------------------------------------------------------------ */
/* The panel                                                           */
/* ------------------------------------------------------------------ */

/**
 * Everything the drill panel renders for one line and month.
 *
 * @param {{label: string, total: object|null, customers: object[]}|null} entry
 * @param {number|null} cardValue  the client's workbook value for the same
 *   line and month, which the page already holds. Never stored in
 *   board_drill; passed in so the two numbers meet only on screen.
 * @param {string} period
 * @returns {{
 *   kind: "detail"|"none"|"unknown",
 *   title: string, caption: string|null, monthLabel: string,
 *   basisLabel: string|null, basisNote: string|null, scopeNote: string|null,
 *   rows: object[], ourTotal: number|null, ourTotalText: string|null,
 *   cardValue: number|null, cardText: string|null,
 *   difference: number|null, differenceText: string|null,
 *   differencePctText: string|null, unit: string,
 *   customerCount: number|null, reason: string|null,
 * }}
 */
export function drillPanel(entry, cardValue, period) {
  const label = monthLabel(period);
  if (entry?.total == null) {
    return {
      kind: "unknown",
      title: NO_DETAIL_TITLE,
      caption:
        "Meridian has no record for this line in this month, so there is nothing to show beside it.",
      monthLabel: label,
      basisLabel: null,
      basisNote: null,
      scopeNote: null,
      rows: [],
      ourTotal: null,
      ourTotalText: null,
      cardValue: toNumber(cardValue),
      cardText: null,
      difference: null,
      differenceText: null,
      differencePctText: null,
      unit: "number",
      customerCount: null,
      reason: null,
    };
  }
  const total = entry.total;
  const unit = text(total.unit) ?? "number";
  const card = toNumber(cardValue);
  if (!isDrillable(entry)) {
    return {
      kind: "none",
      title: NO_DETAIL_TITLE,
      caption: null,
      monthLabel: label,
      basisLabel: null,
      basisNote: null,
      scopeNote: null,
      rows: [],
      ourTotal: null,
      ourTotalText: null,
      cardValue: card,
      cardText: fmtValue(card, unit, null),
      difference: null,
      differenceText: null,
      differencePctText: null,
      unit,
      customerCount: null,
      reason: text(total.reason),
    };
  }
  const ourTotal = toNumber(total.our_total);
  const basis = text(total.drill_basis);
  const difference = ourTotal !== null && card !== null ? ourTotal - card : null;
  return {
    kind: "detail",
    title: PANEL_TITLE,
    caption: entry.customers.length === 0 ? text(total.reason) : null,
    monthLabel: label,
    basisLabel: basis ? (BASIS_LABELS[basis] ?? basis) : null,
    basisNote: basis ? (BASIS_NOTES[basis] ?? null) : null,
    scopeNote: text(total.scope_note),
    rows: entry.customers.map((row) => ({
      key: String(row.customer_id ?? ""),
      name: customerLabel(row),
      isRemainder: row.is_remainder === true,
      rank: toNumber(row.rank),
      value: toNumber(row.value),
      valueText: fmtValue(row.value, unit, null),
    })),
    ourTotal,
    ourTotalText: fmtValue(ourTotal, unit, null),
    cardValue: card,
    cardText: fmtValue(card, unit, null),
    difference,
    differenceText: fmtSigned(difference, unit),
    differencePctText: fmtSignedPct(differencePct(difference, card)),
    unit,
    customerCount: toNumber(total.our_customers),
    reason: null,
  };
}

/**
 * The difference as a share of the CLIENT's figure, or null.
 *
 * Null against a zero or absent card: there is no percentage of nothing, and
 * showing one would manufacture an infinity the reader would take as a fact
 * (the same rule execKpi.momDelta follows for a zero prior month).
 */
export function differencePct(difference, cardValue) {
  const d = toNumber(difference);
  const c = toNumber(cardValue);
  if (d === null || c === null || c === 0) return null;
  return d / Math.abs(c);
}

/** A customer's display name, falling back to the account key we hold. */
export function customerLabel(row) {
  const name = text(row?.customer_name);
  if (name !== null) return name;
  const id = text(row?.customer_id);
  return id === null ? "Unnamed account" : `Account ${id}`;
}

/** A signed value in its unit ("+$1,204", "-3"); no sign when it rounds to zero. */
export function fmtSigned(value, unit) {
  const v = toNumber(value);
  if (v === null) return null;
  const shown = fmtValue(Math.abs(v), unit, null);
  if (shown === null) return null;
  if (!/[1-9]/.test(shown)) return shown;
  return `${v < 0 ? "-" : "+"}${shown}`;
}

/** A fraction as a signed percent with one decimal ("+1.0%", "-3.3%"). */
export function fmtSignedPct(value) {
  const v = toNumber(value);
  if (v === null) return null;
  const body = `${(Math.abs(v) * 100).toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
  if (!/[1-9]/.test(body)) return body;
  return `${v < 0 ? "-" : "+"}${body}`;
}

/**
 * How the difference reads. Deliberately not a verdict: this panel sets two
 * numbers side by side and says what the gap is, and whether a gap matters is
 * the staff variance register's question, not the client page's.
 */
export function differenceNote(panel) {
  if (!panel || panel.kind !== "detail") return null;
  if (panel.ourTotal === null) return "Meridian holds no rows for this scope and month.";
  if (panel.cardValue === null) {
    return "The workbook has no figure for this month, so there is nothing to compare.";
  }
  if (panel.difference === 0) return "Meridian's total matches the workbook figure exactly.";
  return "Meridian's total and the workbook figure are two different measurements of the same scope. A gap is information, not an error.";
}
